import {PayloadAction, createSlice, AnyAction} from '@reduxjs/toolkit';
import {IArticlesState, TArticlePreview, IArticleXData} from '../types';
import {IRootReducer} from 'core/redux/reducers';
import {HYDRATE} from 'next-redux-wrapper';
import {IArticle, IArticleType, ISimilarArticle} from '../models';
import {IProductShort} from 'modules/products/types';
import {ISeo} from 'core/types';

export const articlesModuleName = 'articles';

const initialState: IArticlesState = {
    articleProducts: [],
    articleTypes: [],
    articleXSeo: {metaTitle: '', robots: '', metaDescription: '', pageTitle: ''},
    articlesByCategory: {},
    designType: null,
    isArticlesByTag: false,
    isCategoryType: false,
    seo: {metaTitle: '', robots: '', metaDescription: '', pageTitle: ''},
    similarArticles: [],
};

export const articlesSlice = createSlice({
    extraReducers: (builder) => {
        // eslint-disable-next-line complexity
        builder.addCase(HYDRATE, (state, action: AnyAction) => {
            const payload = action.payload as IRootReducer | undefined;
            if (!payload) {
                return state;
            }

            const articlesState = payload[articlesModuleName] as IArticlesState | undefined;
            if (!articlesState) {
                return state;
            }

            return {
                ...state,
                ...articlesState,
                articleProducts: articlesState.articleProducts?.length
                    ? articlesState.articleProducts
                    : state.articleProducts,
                articleTypes: articlesState.articleTypes?.length ? articlesState.articleTypes : state.articleTypes,
                articleXSeo: Object.keys(articlesState.articleXSeo).length
                    ? articlesState.articleXSeo
                    : state.articleXSeo,
                articlesByCategory: Object.keys(articlesState.articlesByCategory).length
                    ? articlesState.articlesByCategory
                    : state.articlesByCategory,
                designType: articlesState.designType ?? state.designType ?? null,
                isArticlesByTag: articlesState.isArticlesByTag ?? state.isArticlesByTag ?? false,
                isCategoryType: articlesState.isCategoryType ?? state.isCategoryType ?? false,
                seo: Object.keys(articlesState.seo).length ? articlesState.seo : state.seo,
                similarArticles: articlesState.similarArticles?.length
                    ? articlesState.similarArticles
                    : state.similarArticles,
                articleXData: articlesState.articleXData ?? state.articleXData,
            } as IArticlesState;
        });
    },
    initialState,
    name: articlesModuleName,
    reducers: {
        actionSetArticleSeo(state: IArticlesState, {payload}: PayloadAction<ISeo>) {
            state.seo = payload;
        },
        actionSetArticleTypes(state: IArticlesState, {payload}: PayloadAction<IArticleType[]>) {
            state.articleTypes = payload;
        },
        actionSetArticleXProducts(state: IArticlesState, {payload}: PayloadAction<IProductShort[] | undefined>) {
            state.articleProducts = payload;
        },
        actionSetArticlesByCategory(
            state: IArticlesState,
            {payload}: PayloadAction<Record<string, TArticlePreview[]>>,
        ) {
            state.articlesByCategory = payload;
        },
        actionSetArticleXSeo(state: IArticlesState, {payload}: PayloadAction<ISeo>) {
            state.articleXSeo = payload;
        },
        actionSetDesignType(state: IArticlesState, {payload}: PayloadAction<IArticle['designType']>) {
            state.designType = payload;
        },
        actionSetIsArticlesByTag(state: IArticlesState, {payload}: PayloadAction<boolean>) {
            state.isArticlesByTag = payload;
        },
        actionSetIsCategoryType(state: IArticlesState, {payload}: PayloadAction<boolean>) {
            state.isCategoryType = payload;
        },
        actionSetSimilarArticles(state: IArticlesState, {payload}: PayloadAction<ISimilarArticle[]>) {
            state.similarArticles = payload;
        },
        actionSetArticleXData(state: IArticlesState, {payload}: PayloadAction<IArticleXData | undefined>) {
            state.articleXData = payload;
        },
    },
});

export const {
    actions: {
        actionSetArticleTypes,
        actionSetDesignType,
        actionSetArticlesByCategory,
        actionSetIsCategoryType,
        actionSetSimilarArticles,
        actionSetIsArticlesByTag,
        actionSetArticleXProducts,
        actionSetArticleSeo,
        actionSetArticleXSeo,
        actionSetArticleXData,
    },
} = articlesSlice;

export const {reducer: articlesReducer} = articlesSlice;
